import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Send } from 'lucide-react';
import { useCanAccess } from '@/hooks/useCanAccess';
import { useActiveCampaigns, useCancelCampaign, usePauseCampaign, useResumeCampaign } from '@/hooks/useCampaigns';
import type { ActiveCampaign } from '@/services/campaigns';
import { useTenantTimezone } from '@/hooks/useTenantTimezone';
import { tenantShortDateTimeLabel } from '@/lib/tenant-dates';
import {
  Badge,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/v2/design-system/primitives';

/**
 * Story 61.4 (Epic 61, RC-058) — faixa "automação em andamento". Uma por campanha ativa
 * (enviando, pausada ou agendada para as próximas 24 h), em todas as telas: progresso,
 * Pausar/Retomar/Cancelar (só com `campaigns:manage`) e o caminho para "quem recebeu".
 * Sem snooze: enquanto houver lote rodando a clínica vê. Padrão visual das faixas do shell.
 */
export function ActiveCampaignBanner() {
  const podeVer = useCanAccess('campaigns:read');
  const podeGerenciar = useCanAccess('campaigns:manage');
  const { data } = useActiveCampaigns(podeVer);
  if (!podeVer || !data || data.length === 0) return null;
  return (
    <>
      {data.map((c) => (
        <ActiveCampaignRow key={c.id} campaign={c} podeGerenciar={podeGerenciar} />
      ))}
    </>
  );
}

const BTN = 'min-h-11 rounded-so-sm px-3 py-1.5 text-so-12 font-bold sm:min-h-0';

/** A frase da faixa; a Configuração rápida (70.17 AC 4) mostra o lote de reativação com ela (RC-142). */
export function describeActiveCampaign(c: ActiveCampaign, tz: string): string {
  if (c.status === 'sending') {
    const inicio = c.startedAt ? ` · iniciada ${tenantShortDateTimeLabel(c.startedAt, tz)}` : '';
    return `Enviando: ${c.sent} de ${c.totalRecipients} mensagens${inicio}`;
  }
  if (c.status === 'paused') {
    const quando = c.pausedAt ? ` ${tenantShortDateTimeLabel(c.pausedAt, tz)}` : '';
    const motivo =
      c.failedReason === 'WHATSAPP_DISCONNECTED'
        ? ' — WhatsApp desconectado; retoma sozinha ao reconectar'
        : '';
    return `Pausada${quando}: ${c.sent} de ${c.totalRecipients} enviadas${motivo}`;
  }
  const quando = c.scheduledAt ? ` ${tenantShortDateTimeLabel(c.scheduledAt, tz)}` : '';
  return `Sai${quando}: ${c.totalRecipients} clientes vão receber`;
}

function ActiveCampaignRow({ campaign: c, podeGerenciar }: { campaign: ActiveCampaign; podeGerenciar: boolean }) {
  const navigate = useNavigate();
  const tz = useTenantTimezone();
  const pause = usePauseCampaign();
  const resume = useResumeCampaign();
  const cancel = useCancelCampaign();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const restantes = Math.max(c.totalRecipients - c.sent - c.failed, 0);

  return (
    <>
      <div
        role="status"
        data-testid="active-campaign-banner"
        className="flex flex-wrap items-start gap-3 border-b border-sober-outline bg-sober-surface px-4 py-3 text-so-14 text-sober-text lg:px-8"
      >
        <Send className="mt-0.5 size-4 shrink-0 text-sober-primary" aria-hidden />
        <div className="min-w-0 flex-1 basis-full sm:basis-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-bold">{c.name}</span>
            {c.origin === 'automation' && <Badge variant="gray">Automática</Badge>}
          </div>
          <p className="mt-1 text-sober-secondary">{describeActiveCampaign(c, tz)}</p>
        </div>
        {/* Ações em linha própria no celular (≤ 375 px): basis-full; ao lado no desktop. */}
        <div className="flex basis-full flex-wrap gap-2 sm:basis-auto" data-testid="active-campaign-actions">
          {podeGerenciar && c.status === 'sending' && (
            <button type="button" onClick={() => pause.mutate(c.id)} disabled={pause.isPending} className={`${BTN} text-sober-secondary hover:bg-sober-muted`}>
              Pausar
            </button>
          )}
          {podeGerenciar && c.status === 'paused' && (
            <button type="button" onClick={() => resume.mutate(c.id)} disabled={resume.isPending} className={`${BTN} bg-sober-primary-fg text-white hover:opacity-90`}>
              Retomar
            </button>
          )}
          {podeGerenciar && (
            <button type="button" onClick={() => setConfirmOpen(true)} className={`${BTN} text-sober-red hover:bg-sober-muted`}>
              Cancelar
            </button>
          )}
          <button type="button" onClick={() => navigate(`/campanhas/${c.id}`)} className={`${BTN} text-sober-secondary hover:bg-sober-muted`}>
            Ver quem recebeu
          </button>
        </div>
      </div>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent size="sm">
          <DialogHeader>
            <DialogTitle>Cancelar {c.name}?</DialogTitle>
            <DialogDescription>
              {restantes === 1
                ? 'A mensagem restante não será enviada e fica registrada como não enviada.'
                : `As ${restantes} mensagens restantes não serão enviadas e ficam registradas como não enviadas.`}
            </DialogDescription>
          </DialogHeader>
          <DialogBody>
            <p className="text-so-14 text-sober-secondary">Quem já recebeu continua com a mensagem. Você pode ver a lista completa em Campanhas.</p>
          </DialogBody>
          <DialogFooter>
            <button type="button" onClick={() => setConfirmOpen(false)} className={`${BTN} text-sober-secondary hover:bg-sober-muted`}>
              Manter enviando
            </button>
            <button
              type="button"
              disabled={cancel.isPending}
              onClick={() => cancel.mutate(c.id, { onSuccess: () => setConfirmOpen(false) })}
              className={`${BTN} bg-sober-red text-white hover:opacity-90 disabled:opacity-50`}
            >
              Cancelar lote
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
